import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Stack
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon
} from "@mui/icons-material";
import { useUploadMedia } from "./hooks/useUploadMedia";

export const MediaUploader = ({ onUploadSuccess }) => {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const uploadMutation = useUploadMedia();

  const handleFileUpload = async (file) => {
    if (!file) { return; }

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return;
    }

    setError("");

    try {
      const result = await uploadMutation.mutateAsync({ file });

      const mediaItem = {
        url: result.assetUrl,
        type: "image",
        order: 0
      };

      onUploadSuccess(mediaItem);
    } catch (err) {
      setError(err.message || "Upload failed. Please try again.");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = "";
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      <Paper
        sx={{
          p: 3,
          border: dragOver ? "2px dashed #1976d2" : "2px dashed #ccc",
          borderRadius: 2,
          backgroundColor: dragOver ? "rgba(25, 118, 210, 0.05)" : "transparent",
          cursor: "pointer",
          transition: "all 0.2s ease",
          position: "relative"
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={openFileDialog}
      >
        {uploadMutation.isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              borderRadius: 2,
              zIndex: 1
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={40} />
              <Typography variant="body2">Uploading...</Typography>
            </Stack>
          </Box>
        )}

        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 2,
              borderRadius: "50%",
              backgroundColor: dragOver ? "#1976d2" : "#f5f5f5",
              color: dragOver ? "white" : "#666"
            }}
          >
            {dragOver ? <UploadIcon fontSize="large" /> : <ImageIcon fontSize="large" />}
          </Box>

          <Stack alignItems="center" spacing={1}>
            <Typography variant="h6" color={dragOver ? "primary" : "textPrimary"}>
              {dragOver ? "Drop files here" : "Upload Media"}
            </Typography>

            <Typography variant="body2" color="textSecondary" textAlign="center">
              Drag & drop files here, or click to select
              <br />
              Supports images (max 10MB)
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              openFileDialog();
            }}
          >
            Choose Files
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
